import React, { useCallback, useMemo, useState } from 'react';

import classnames from 'classnames';
import omit from 'lodash/omit';
import Overflow from 'rc-overflow';

import DateRangePicker from 'components/uiLibrary/DateRangePicker';
import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import Modal from 'components/uiLibrary/Modal';
import Typography from 'components/uiLibrary/Typography';

// eslint-disable-next-line import/no-cycle
import { SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import useQuickView, { QUICK_VIEW_TYPES } from 'components/Globals/Layout/QuickView';

import { AGGREGATION_ON, SORT_OPTION_VALUES, TP } from 'constants/index';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import usePageContext from 'utils/hooks/usePageContext';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Productions/queries';

import { useTranslation } from 'src/i18n';

import {
  AGGREGATION_TYPES,
  FILTER_TYPES,
  FILTER_TYPE_LABEL_PREFIX,
  FILTER_TYPE_ENTITY_TYPE_MAP,
  ROUTE_DATE_FORMATS,
  ROUTE_RESERVED_KEYWORDS,
} from 'operabase-router/constants';

import QuickFilterChip, { ACTIONS } from '../QuickFilterChip';

import classes from './QuickFilters.module.scss';

const LEFT_ICON_MAP = {
  [FILTER_TYPES.SEASON]: 'calendar',
};

export const SPECIAL_OPTION_IDS = {
  ALL: 'ALL',
  QUICK_VIEW_FILTER: 'QUICK_VIEW_FILTER',
  DATE_RANGE: 'DATE_RANGE',
};

const QuickFilterOptionWrapper = props => {
  if (props?.isCollapsable) {
    return (
      <div className={classnames(classes.collapsableItem, { [classes.collapsableItem__first]: props?.index === 0 })}>
        <QuickFilterOptionWrapper {...props} isCollapsable={false} />
      </div>
    );
  }

  const { filterType, variant, option, className, ...rest } = props;

  return (
    <QuickFilterChip
      {...rest}
      option={option}
      showCloseIcon={option?.showCloseIcon || false}
      rightIcon={option?.rightIcon}
      leftIcon={option?.isQuickView ? null : option?.leftIcon || LEFT_ICON_MAP[filterType]}
      variant={option?.variant || variant}
      className={classnames({
        [classes.skipPaddingLeft]: props?.index === 0 && props?.skipBorder,
        [className]: className,
      })}
    />
  );
};

const QuickFilterAllOptions = ({
  title,
  onClose,
  filterType,
  showCount,
  getFilterChipLinkProps,
  onOptionClickHandler,
  queryFn,
  trackingData,
  getOptions,
}) => {
  const { navigate } = usePageContext();
  const { obRouteContext } = useAppContext();
  const selectedDate = useMemo(() => {
    const { linkProps } = obRouteContext || {};
    const { [FILTER_TYPES.DATE]: dateFilters } = linkProps?.filters || {};

    if (dateFilters?.date_from && dateFilters?.date_to) {
      return {
        fromDate: dateFilters?.date_from,
        toDate: dateFilters?.date_to,
      };
    }

    return {};
  }, [obRouteContext]);

  const { data, isFetching: isFetchingOptions } = useQuery(queryFn({ fetchLimit: 1000 }));

  const options = useMemo(() => {
    if (isFetchingOptions) {
      return [];
    }

    return getOptions({ filterOptions: data, includeAllOption: false });
  }, [data, getOptions, isFetchingOptions]);

  const onDateChange = useCallback(
    ({ fromDate, toDate }) => {
      navigate.to(
        getFilterChipLinkProps({
          option: {
            id: SPECIAL_OPTION_IDS.DATE_RANGE,
            filters: { [FILTER_TYPES.DATE]: { date_from: fromDate, date_to: toDate } },
          },
          action: ACTIONS.SELECT,
        }),
      );
      onClose();
    },
    [getFilterChipLinkProps, navigate, onClose],
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      styles={{
        modalContainer: classes.optionsModalContainer,
        modalContent: classes.optionsModalContent,
      }}
      title={
        <Typography variant="h2" size={16} weight="bold">
          {title}
        </Typography>
      }
      allowMobileDrawer
    >
      <div>
        {[FILTER_TYPES.DATE, FILTER_TYPES.SEASON].includes(filterType) && (
          <div className={classes.dateRangePicker}>
            <DateRangePicker date={selectedDate} onChange={onDateChange} />
          </div>
        )}
        <div className={classes.optionsList}>
          {options?.map((option, index) => {
            if (option?.id === SPECIAL_OPTION_IDS.QUICK_VIEW_FILTER) {
              return null;
            }

            return (
              <QuickFilterOptionWrapper
                key={option.id}
                index={index}
                className={classes.optionsList__item}
                option={{ ...option, showCloseIcon: false }}
                onClick={onOptionClickHandler}
                getLinkProps={getFilterChipLinkProps}
                showCount={showCount}
                filterType={filterType}
                trackingData={trackingData}
              />
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

const QuickFilters = ({
  filterType,
  overrideApplyFilterType,
  limit = 10,
  baseLinkProps,
  inline = false,
  collapsable = false,
  scrollable = false,
  onOptionClick,
  aggregationOn = AGGREGATION_ON.PRODUCTIONS,
  withAppliedFilters = true,
  prefixOptions = [],
  suffixOptions = [],
  showQuickViewFilterOption = false,
  hasCustomViewAllHandler = false,
  showCount = false,
  titleKey = `${TP}.FN_FILTER_PERFORMANCE_BY_SEO_LABEL`,
  variant,
  includeSpecialOptions = false,
  skipBorder = false,
  enableCache = false,
  trackingData = {},
  quickViewFilterOptionProps,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { navigate } = usePageContext();
  const { obRouteContext } = useAppContext();
  const { linkProps, filterParams, page } = obRouteContext || {};
  const stats = page?.stats || {};
  const { filters: routeFilters } = linkProps || {};
  const { showQuickView } = useQuickView();
  const [showAllOptions, setShowAllOptions] = useState(false);
  const relevantFilterParams = useMemo(() => {
    const { ctx_entity, search_exp } = filterParams || {};

    return {
      ctx_entity,
      ...(enableCache && { car: true }),
      search_exp,
    };
  }, [filterParams, enableCache]);

  const appliedFilterTypeId = useMemo(() => {
    if (!withAppliedFilters || !filterType) {
      return null;
    }

    if ([FILTER_TYPES.DATE, FILTER_TYPES.SEASON].includes(filterType)) {
      return routeFilters?.[FILTER_TYPES.DATE]?.path;
    }

    return routeFilters?.[overrideApplyFilterType || filterType]?.[0]?.entity?.id || null;
  }, [withAppliedFilters, overrideApplyFilterType, filterType, routeFilters]);

  const { hasTicketSelected, hasOnlineSelected, hasUpcomingSelected, hasArchivesSelected } = useMemo(() => {
    const today = createDate();

    return {
      hasTicketSelected: routeFilters?.[FILTER_TYPES.TICKET] === true,
      hasOnlineSelected: routeFilters?.[FILTER_TYPES.WATCH_ONLINE] === true,
      hasUpcomingSelected:
        !routeFilters?.[FILTER_TYPES.DATE]?.date_to &&
        routeFilters?.[FILTER_TYPES.DATE]?.date_from &&
        createDate(routeFilters?.[FILTER_TYPES.DATE]?.date_from).isSame(today, 'day'),
      hasArchivesSelected: routeFilters?.[FILTER_TYPES.DATE]?.path === ROUTE_RESERVED_KEYWORDS.archives,
    };
  }, [routeFilters]);

  const getFilterOptionsQuery = useCallback(
    ({ fetchLimit }) =>
      queries.getQuickFilterOptions({
        aggregationOn,
        aggregationType: AGGREGATION_TYPES[filterType],
        filters: relevantFilterParams,
        ...(filterType === FILTER_TYPES.SEASON && { sort: SORT_OPTION_VALUES.YEAR_DESC }),
        limit: fetchLimit,
        queryConfig: { enabled: !!filterType, keepPreviousData: true },
      }),
    [aggregationOn, filterType, relevantFilterParams],
  );

  const { data, isFetching: isFetchingOptions } = useQuery(getFilterOptionsQuery({ fetchLimit: limit }));

  const { data: hasTickets, isFetching: isFetchingHasTickets } = useQuery(
    queries.getPageProductionStats({
      filters: {
        ...relevantFilterParams,
        date_from: createDate().format(ROUTE_DATE_FORMATS.STANDARD),
        facets: 'has_tickets',
      },
      queryConfig: {
        enabled: !!filterType && !hasTicketSelected,
        select: response => response?.meta?.has_tickets > 0,
        ...(stats?.search?.tickets >= 0 && {
          initialData: {
            meta: {
              has_tickets: stats?.search?.tickets,
            },
          },
        }),
        keepPreviousData: true,
      },
    }),
  );

  const { data: hasOnline, isFetching: isFetchingHasOnline } = useQuery(
    queries.getPageProductionStats({
      filters: {
        ...relevantFilterParams,
        facets: 'has_online',
      },
      queryConfig: {
        enabled: !!filterType && !hasOnlineSelected,
        select: response => response?.meta?.has_online > 0,
        ...(stats?.search?.online >= 0 && {
          initialData: {
            meta: {
              has_online: stats?.search?.online,
            },
          },
        }),
        keepPreviousData: true,
      },
    }),
  );

  const getFilterChipLinkProps = useCallback(
    ({ option, action }) => {
      if (option?.filters) {
        if (action === ACTIONS.DESELECT) {
          return navigate.getLinkProps({
            baseRoute: ROUTE_RESERVED_KEYWORDS.productions,
            ...baseLinkProps,
            filters: {
              ...omit(routeFilters, Object.keys(option?.filters)),
              ...(baseLinkProps?.filters || {}),
            },
          });
        }

        return navigate.getLinkProps({
          ...baseLinkProps,
          filters: {
            ...routeFilters,
            ...(baseLinkProps?.filters || {}),
            ...option?.filters,
          },
        });
      }

      if (!option?.isCore) {
        return null;
      }

      if (filterType === FILTER_TYPES.SEASON) {
        return navigate.getLinkProps({
          ...baseLinkProps,
          filters: {
            ...routeFilters,
            ...(baseLinkProps?.filters || {}),
            [FILTER_TYPES.WATCH_ONLINE]: null,
            [FILTER_TYPES.TICKET]: null,
            [FILTER_TYPES.DATE]:
              action === ACTIONS.DESELECT
                ? {}
                : {
                    date_from: createDate(option?.id)
                      .startOf('year')
                      .format(ROUTE_DATE_FORMATS.STANDARD),
                    date_to: createDate(option?.id)
                      .endOf('year')
                      .format(ROUTE_DATE_FORMATS.STANDARD),
                  },
          },
        });
      }

      return navigate.getLinkProps({
        ...baseLinkProps,
        filters: {
          ...routeFilters,
          ...(baseLinkProps?.filters || {}),
          [overrideApplyFilterType || filterType]:
            action === ACTIONS.DESELECT
              ? []
              : [
                  {
                    entityType: FILTER_TYPE_ENTITY_TYPE_MAP[filterType],
                    entity: option,
                  },
                ],
        },
      });
    },
    [filterType, overrideApplyFilterType, navigate, baseLinkProps, routeFilters],
  );

  const onOptionClickHandler = useCallback(
    (e, option, { action }) => {
      if (showAllOptions) {
        setShowAllOptions(false);
      }

      if (option?.isCustomClick && option?.onClick) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        option.onClick();
        return;
      }

      if (
        (option?.id !== SPECIAL_OPTION_IDS.ALL || !hasCustomViewAllHandler) &&
        (option?.isQuickView || option?.filterType)
      ) {
        if (
          [FILTER_TYPES.SEASON, FILTER_TYPES.DATE, FILTER_TYPES.WORK_TYPE, FILTER_TYPES.COUNTRY].includes(
            option?.filterType,
          )
        ) {
          setShowAllOptions(true);
          return;
        }

        showQuickView({
          type: QUICK_VIEW_TYPES.FILTERS,
          data: {
            defaultExpandedFilter: option?.filterType || filterType,
            // NOTE: This is used to scroll to the correct filter in quick view filter in case of venue
            defaultSubFilterType: option?.subFilterType ? option?.subFilterType : null,
          },
        });

        return;
      }

      if (typeof onOptionClick === 'function') {
        onOptionClick(e, option, { action });
      }

      if (action !== ACTIONS.SELECT) {
        navigate.to(getFilterChipLinkProps({ option, action }));
      }
    },
    [
      onOptionClick,
      filterType,
      showQuickView,
      navigate,
      getFilterChipLinkProps,
      hasCustomViewAllHandler,
      showAllOptions,
    ],
  );

  const isCollapsable = collapsable && !scrollable;

  const getOptionTitle = useCallback(
    ({ option, filterTypeLabel = '' }) => {
      if (!option?.name || option?.id === SPECIAL_OPTION_IDS.QUICK_VIEW_FILTER) {
        return null;
      }

      let title = '';

      if (filterTypeLabel) {
        title = t(filterTypeLabel);
      }

      if (option?.name) {
        if (title?.length > 0) {
          title += ' ';
        }

        title += `"${option.name}"`;
      }

      let label = t(titleKey, { filterType: title });

      if (showCount && option?.count > 0) {
        label += ` (${option?.count})`;
      }

      return label;
    },
    [t, showCount, titleKey],
  );

  const specialFilterOptions = useMemo(() => {
    if (!includeSpecialOptions) {
      return [];
    }

    const today = createDate();
    const currentYear = today.year();

    const hasCurrentOrUpcomingYear = data?.some(option => Number(option?.id) >= currentYear);

    return [
      ...(filterType !== FILTER_TYPES.SEASON || hasCurrentOrUpcomingYear
        ? [
            {
              name: t(`${TP}.FN_UPCOMING_PERFORMANCES`),
              title: t(`${TP}.FN_FILTER_PERFORMANCE_BY_SEO_LABEL`, { filterType: t(`${TP}.FN_UPCOMING_PERFORMANCES`) }),
              filters: {
                [FILTER_TYPES.DATE]: {
                  date_from: today.format(ROUTE_DATE_FORMATS.STANDARD),
                },
                [FILTER_TYPES.TICKET]: null,
                [FILTER_TYPES.WATCH_ONLINE]: null,
              },
              selected: withAppliedFilters && hasUpcomingSelected,
              showCloseIcon: false,
            },
          ]
        : []),
      ...(hasTickets || hasTicketSelected
        ? [
            {
              name: t(`${TP}.FN_TICKETS`),
              title: t(`${TP}.FN_FILTER_PERFORMANCE_BY_SEO_LABEL`, { filterType: t(`${TP}.FN_TICKETS`) }),
              leftIcon: 'tickets',
              filters: {
                [FILTER_TYPES.TICKET]: true,
                [FILTER_TYPES.WATCH_ONLINE]: null,
              },
              selected: withAppliedFilters && hasTicketSelected,
              showCloseIcon: true,
            },
          ]
        : []),
      ...(hasOnline || hasOnlineSelected
        ? [
            {
              name: t(`${TP}.FN_WATCH_ONLINE_VIDEOS`),
              title: t(`${TP}.FN_FILTER_PERFORMANCE_BY_SEO_LABEL`, { filterType: t(`${TP}.FN_WATCH_ONLINE_VIDEOS`) }),
              leftIcon: 'play_circle_outline',
              filters: {
                [FILTER_TYPES.TICKET]: null,
                [FILTER_TYPES.WATCH_ONLINE]: true,
                [FILTER_TYPES.DATE]: null,
              },
              selected: withAppliedFilters && hasOnlineSelected,
              showCloseIcon: true,
            },
          ]
        : []),
      {
        name: t(`${TP}.ARCHIVES`),
        title: t(`${TP}.FN_FILTER_PERFORMANCE_BY_SEO_LABEL`, { filterType: t(`${TP}.FN_PAST_PERFORMANCES`) }),
        filters: {
          [FILTER_TYPES.TICKET]: null,
          [FILTER_TYPES.WATCH_ONLINE]: null,
          [FILTER_TYPES.DATE]: {
            date_to: today.format(ROUTE_DATE_FORMATS.STANDARD),
            path: ROUTE_RESERVED_KEYWORDS.archives,
          },
        },
        selected: withAppliedFilters && hasArchivesSelected,
        showCloseIcon: true,
      },
    ];
  }, [
    t,
    includeSpecialOptions,
    hasTickets,
    hasOnline,
    hasTicketSelected,
    hasOnlineSelected,
    hasUpcomingSelected,
    hasArchivesSelected,
    filterType,
    data,
    withAppliedFilters,
  ]);

  const getOptions = useCallback(
    ({ filterOptions, includeAllOption = true }) => {
      const filterTypeOptions =
        filterOptions?.map(option => ({
          ...option,
          title: getOptionTitle({ option, filterTypeLabel: FILTER_TYPE_LABEL_PREFIX[filterType] }),
          count: option?.total || 0,
          selected: appliedFilterTypeId === option.id,
          showCloseIcon: true,
          isCore: true,
        })) || [];

      let quickViewFilterStartOptions = [];
      let quickViewFilterEndOptions = [];

      if (includeAllOption) {
        const quickViewOption = {
          id: SPECIAL_OPTION_IDS.QUICK_VIEW_FILTER,
          name: quickViewFilterOptionProps?.name || `${t(`${TP}.m_ALL`)} ${t(`${TP}.FILTERS`)}`,
          rightIcon: 'chevron_right',
          isQuickView: true,
          variant: quickViewFilterOptionProps?.variant,
          filterType,
        };

        const includeStartOption =
          quickViewFilterOptionProps?.startPlacement || !quickViewFilterOptionProps?.endPlacement;

        if (includeStartOption) {
          quickViewFilterStartOptions = [quickViewOption];
        }

        if (quickViewFilterOptionProps?.endPlacement) {
          quickViewFilterEndOptions = [
            {
              ...quickViewOption,
              variant: includeStartOption ? 'span' : quickViewFilterOptionProps?.variant,
            },
          ];
        }
      }

      return [
        ...quickViewFilterStartOptions,
        ...specialFilterOptions,
        ...prefixOptions,
        ...filterTypeOptions,
        ...suffixOptions,
        ...quickViewFilterEndOptions,
      ];
    },
    [
      t,
      filterType,
      getOptionTitle,
      specialFilterOptions,
      prefixOptions,
      suffixOptions,
      appliedFilterTypeId,
      quickViewFilterOptionProps,
    ],
  );

  const options = useMemo(() => {
    if (
      (isFetchingOptions && !data?.length) ||
      (isFetchingHasTickets && !hasTickets) ||
      (isFetchingHasOnline && !hasOnline)
    ) {
      return [];
    }

    return getOptions({ filterOptions: data, includeAllOption: showQuickViewFilterOption });
  }, [
    data,
    getOptions,
    hasOnline,
    hasTickets,
    isFetchingHasOnline,
    isFetchingHasTickets,
    isFetchingOptions,
    showQuickViewFilterOption,
  ]);

  const optionTrackingData = useMemo(
    () => ({
      ...trackingData,
      subComponent: showQuickViewFilterOption ? SUB_COMPONENTS.VIEW_CTA : SUB_COMPONENTS.NAVIGATE_CTA,
    }),
    [trackingData, showQuickViewFilterOption],
  );

  return (
    <>
      <HorizontalScroller disabled={!scrollable} hideArrow>
        <Overflow
          className={classnames(classes.root, {
            [classes.inline]: inline || collapsable || scrollable,
            [classes.collapsable]: isCollapsable,
            [classes.scrollable]: scrollable,
          })}
          data={options}
          renderRest={() => (
            <div className={classnames(classes.collapsableItem, classes.collapsableItem__viewAll)}>
              <QuickFilterChip
                option={{ id: SPECIAL_OPTION_IDS.ALL, name: t(`${TP}.VIEW_ALL`), filterType }}
                onClick={onOptionClickHandler}
                getLinkProps={getFilterChipLinkProps}
                rightIcon="chevron_right"
                skipBorder={skipBorder}
                trackingData={{
                  ...trackingData,
                  subComponent: SUB_COMPONENTS.VIEW_ALL_CTA,
                }}
              />
            </div>
          )}
          renderItem={(option, { index }) => (
            <QuickFilterOptionWrapper
              key={option.id}
              index={index}
              option={option}
              onClick={onOptionClickHandler}
              getLinkProps={getFilterChipLinkProps}
              isCollapsable={isCollapsable}
              showCount={showCount}
              skipBorder={skipBorder}
              filterType={filterType}
              variant={variant}
              trackingData={optionTrackingData}
            />
          )}
          maxCount={isCollapsable ? 'responsive' : null}
          component="ul"
          itemComponent="li"
        />
      </HorizontalScroller>

      {showAllOptions && (
        <QuickFilterAllOptions
          title={quickViewFilterOptionProps?.name || `${t(`${TP}.m_ALL`)} ${t(`${TP}.FILTERS`)}`}
          onClose={() => setShowAllOptions(false)}
          filterType={filterType}
          showCount={showCount}
          getFilterChipLinkProps={getFilterChipLinkProps}
          onOptionClickHandler={onOptionClickHandler}
          trackingData={optionTrackingData}
          queryFn={getFilterOptionsQuery}
          getOptions={getOptions}
        />
      )}
    </>
  );
};

export default QuickFilters;
